/* eslint-disable react/react-in-jsx-scope */
import * as pdfjsLib from "pdfjs-dist";
import { useEffect, useRef, useState } from "react";

import IconPdf from "@/assets/icones/pdf.svg?react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import worker from "@/pdf-worker";
import { Download, Eye, MoreVertical } from "lucide-react";
pdfjsLib.GlobalWorkerOptions.workerPort = new worker();

type pdfThumbnailProps = {
  url: string;
  extension: string | undefined;
  treeFinancer: any;
  pj: any;
  otherpage?: boolean;
};

export const PdfThumbnail = ({
  url,
  extension,
  treeFinancer,
  pj,
  otherpage = false,
}: pdfThumbnailProps) => {
  const thumbRef = useRef<HTMLCanvasElement>(null);
  const fullContainerRef = useRef<HTMLDivElement>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [isThumbLoading, setIsThumbLoading] = useState(true);
  // thumbnail
  useEffect(() => {
    const renderThumb = async () => {
      const pdf = await pdfjsLib.getDocument(url).promise;
      const page = await pdf.getPage(1);
      const viewport = page.getViewport({ scale: 0.4 });
      const canvas = thumbRef.current;
      if (!canvas) return;
      const ctx = canvas.getContext("2d")!;
      canvas.width = viewport.width;
      canvas.height = viewport.height;
      await page.render({ canvasContext: ctx, viewport }).promise;
    };
    renderThumb().catch(console.error).finally(() => setIsThumbLoading(false));;
  }, [url]);

  // rendu complet
  useEffect(() => {
    if (!modalOpen) return;
    const renderAll = async () => {
      const pdf = await pdfjsLib.getDocument(url).promise;
      const container = fullContainerRef.current;
      if (!container) return;
      container.innerHTML = "";
      for (let p = 1; p <= pdf.numPages; p++) {
        const page = await pdf.getPage(p);
        const viewport = page.getViewport({ scale: 1.0 });
        const canvas = document.createElement("canvas");
        canvas.style.display = "block";
        canvas.style.margin = "auto";
        canvas.width = viewport.width;
        canvas.height = viewport.height;
        container.appendChild(canvas);
        await page.render({ canvasContext: canvas.getContext("2d")!, viewport })
          .promise;
      }
    };
    renderAll().catch(console.error);
  }, [modalOpen, url]);

  return (
    <Dialog open={modalOpen} onOpenChange={setModalOpen}>
      <DialogTrigger asChild>
        <div className={`bg-muted  ${!otherpage && "p-3"} rounded-md relative`}>
          <div className="flex justify-between items-center relative z-10 mb-2">
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div
                    className="flex items-center gap-3 "
                    style={{ width: "calc(100% - 45px)" }}
                  >
                    <IconPdf width={18} height={18} />
                    <h3 className="truncate flex-1">{pj.name}</h3>
                  </div>
                </TooltipTrigger>
                <TooltipContent className="flex flex-col gap-2">
                  <div>
                    Porteur de projet :{" "}
                    <strong>
                      {treeFinancer?.find((t: any) => t.id === pj.pp)?.name}
                    </strong>
                  </div>
                  <div>
                    Programme :{" "}
                    <strong>
                      {
                        treeFinancer
                          ?.find((t: any) => t.id === pj.pp)
                          ?.programs.find((p: any) => p.id === pj.program)?.name
                      }
                    </strong>
                  </div>
                  <div>
                    Projet :{" "}
                    <strong>
                      {
                        treeFinancer
                          ?.find((t: any) => t.id === pj.pp)
                          ?.programs.find((p: any) => p.id === pj.program)
                          ?.projects.find((p: any) => p.id === pj.project)?.name
                      }
                    </strong>
                  </div>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
            {!otherpage && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    className="px-2 outline-none border-0 border-none ring-0 hover:bg-black/5 w-auto h-auto"
                  >
                    <MoreVertical className="text-foreground" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  className="flex flex-col gap-2"
                  align="end"
                >
                  <DropdownMenuItem asChild>
                    <a
                      href={url}
                      className="flex gap-2 items-center cursor-pointer text-foreground"
                    >
                      <Eye width={22} height={22} /> Ouvrir
                    </a>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <a
                      href={url}
                      download
                      className="flex gap-2 items-center cursor-pointer text-foreground"
                    >
                      <Download width={22} height={22} /> Télécharger
                    </a>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>

          <div className="relative h-[17rem] overflow-hidden rounded-md bg-white flex items-center justify-center">
            <div style={{ cursor: "pointer", display: "inline-block" }}>
              <canvas
                ref={thumbRef}
                style={{ borderRadius: 4, width: "248px", height: "272px" }}
                className="w-full h-full"
              />
            </div>
          </div>
        </div>
      </DialogTrigger>

      <DialogContent className="sm:max-w-4xl sm:w-full h-[90vh]">
        <DialogHeader>
          <DialogTitle>Aperçu complet du PDF</DialogTitle>
          <DialogDescription>Affichage de toutes les pages</DialogDescription>
        </DialogHeader>

        <div
          ref={fullContainerRef}
          style={{ overflow: "auto", maxHeight: "80vh" }}
        />

        <DialogClose asChild>
          <Button variant="outline" className="mt-4 w-max m-auto">
            Fermer
          </Button>
        </DialogClose>
      </DialogContent>
    </Dialog>
  );
};

export default PdfThumbnail;
